import { createSlice } from '@reduxjs/toolkit';
import { LOADING_STATUS } from '../../../shared/constants/loadingStatus';
import { resolveTabKey } from './lib/resolveTabKey';
import { CHANNEL } from '../constants/messageTemplateKeys';
import { createFacetsTabState } from './lib/createTabState';
import {
  templatesFiltersChanged,
  templatesOrderingChanged,
  templatesSearchChanged,
  templatesTagsModeChanged,
} from './templatesQuerySlice';

const initialState = {
  tabs: {
    email: createFacetsTabState(),
    text: createFacetsTabState(),
  },
};

function resetAllTabFacets(state) {
  for (const key of Object.keys(state.tabs)) {
    state.tabs[key].facetsStatus = LOADING_STATUS.IDLE;
    state.tabs[key].facetsRequestId = null;
  }
}

export const templatesFacetsSlice = createSlice({
  name: 'templatesFacets',
  initialState,
  reducers: {
    templatesFacetsRequested(state, action) {
      const { tabKey, requestId } = action.payload ?? {};
      const resolved = resolveTabKey(tabKey ?? CHANNEL.EMAIL);
      const tab = state.tabs?.[resolved] ?? createFacetsTabState();
      tab.facetsStatus = LOADING_STATUS.LOADING;
      tab.facetsRequestId = requestId ?? null;
      state.tabs[resolved] = tab;
    },
    templatesFacetsReceived(state, action) {
      const { tabKey, requestId, payload } = action.payload ?? {};
      const resolved = resolveTabKey(tabKey);
      const tab = state.tabs?.[resolved] ?? createFacetsTabState();
      // Same out-of-order guard as the list: drop responses from superseded requests.
      if (requestId !== tab.facetsRequestId) return;
      tab.facetsStatus = LOADING_STATUS.SUCCEEDED;
      tab.facets = payload ?? null;
      state.tabs[resolved] = tab;
    },
    templatesFacetsFailed(state, action) {
      const { tabKey, requestId } = action.payload ?? {};
      const resolved = resolveTabKey(tabKey);
      const tab = state.tabs?.[resolved] ?? createFacetsTabState();
      if (requestId !== tab.facetsRequestId) return;
      tab.facetsStatus = LOADING_STATUS.FAILED;
      state.tabs[resolved] = tab;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(templatesSearchChanged, resetAllTabFacets)
      .addCase(templatesOrderingChanged, resetAllTabFacets)
      .addCase(templatesFiltersChanged, resetAllTabFacets)
      .addCase(templatesTagsModeChanged, resetAllTabFacets);
  },
});

export const templatesFacetsReducer = templatesFacetsSlice.reducer;
export const { templatesFacetsRequested, templatesFacetsReceived, templatesFacetsFailed } =
  templatesFacetsSlice.actions;

export const selectTemplatesFacetsTabs = (state) => state.templates.facets.tabs;

function selectActiveTabKey(state) {
  return state.templates.query.activeTab ?? CHANNEL.EMAIL;
}

function selectActiveTabState(state) {
  const tabKey = selectActiveTabKey(state);
  return state.templates.facets.tabs?.[tabKey] ?? createFacetsTabState();
}

export const selectTemplatesFacetsStatus = (state) => selectActiveTabState(state).facetsStatus;
export const selectTemplatesFacets = (state) => selectActiveTabState(state).facets;
